import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useCallback, useEffect, useState } from "react";
import { useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useFragment } from "react-relay";

import { MemberListModal } from "@/features/MemberListModal";
import { useGetTabBarItems } from "@/features/Navigation/hooks/useGetTabBarItems";
import { Button } from "@whoosh/design-system/src/components/controls";
import { CloseIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Modal } from "@whoosh/design-system/src/components/overlay/Modal/Modal";
import { useModalTransition } from "@whoosh/design-system/src/components/overlay/Modal/ModalTransitionContext";
import { Overlay } from "@whoosh/design-system/src/components/overlay/Overlay/Overlay";
import { Box, VStack } from "@whoosh/design-system/src/components/primitive";
import { AnimationDurations } from "@whoosh/design-system/src/theme/animations";
import { theme } from "@whoosh/design-system/src/theme/restyle/theme";

import { NavigationMenu_session$key } from "./__generated__/NavigationMenu_session.graphql";
import { NavigationMenuClubCard } from "./components/NavigationMenuClubCard/NavigationMenuClubCard";
import { NavigationMenuItem } from "./components/NavigationMenuItem/NavigationMenuItem";
import { NavigationMenuJoinCard } from "./components/NavigationMenuJoinCard/NavigationMenuJoinCard";
import { NavigationMenuPanel } from "./components/NavigationMenuPanel/NavigationMenuPanel";

const DESKTOP_PANEL_WIDTH = 288;
const CLOSE_ANIMATION_BUFFER_MS = 50;
const CLOSE_ANIMATION_DURATION_MS =
  AnimationDurations.moderate02 + CLOSE_ANIMATION_BUFFER_MS;

type NavigationMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  sessionRef: NavigationMenu_session$key;
  isMember: boolean;
  onSignIn: () => void;
};

export const NavigationMenu = ({
  isOpen,
  onClose,
  sessionRef,
  isMember,
  onSignIn,
}: NavigationMenuProps) => {
  const session = useFragment(NavigationMenuFragmentString, sessionRef);
  const { runAfterIdle } = useModalTransition();

  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);

  const { top: safeAreaTop } = useSafeAreaInsets();
  const { width: screenWidth } = useWindowDimensions();
  const isIpadOrSmaller = screenWidth < theme.breakpoints.md;
  const panelWidth = isIpadOrSmaller ? screenWidth : DESKTOP_PANEL_WIDTH;

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      return undefined;
    }

    if (!shouldRender) {
      return undefined;
    }

    setIsClosing(true);

    const closeTimer = setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
    }, CLOSE_ANIMATION_DURATION_MS);

    return () => clearTimeout(closeTimer);
  }, [isOpen, shouldRender]);

  const openMemberModal = useCallback(() => {
    setIsMemberModalOpen(true);
  }, []);

  const { items, overflowItems } = useGetTabBarItems({
    navigationScope: "clubStack",
    openMemberModal,
    sessionRef: session,
  });

  const allItems = [...items, ...overflowItems];

  const handleItemPress = useCallback(
    (handler: () => void) => {
      onClose();
      runAfterIdle(handler);
    },
    [onClose, runAfterIdle]
  );

  return (
    <>
      {shouldRender && (
        <Modal
          isOpen={isOpen || isClosing}
          onClose={onClose}
          presentation="fullscreen"
        >
          <Box flex={1} position="relative">
            <Overlay isClosing={isClosing} onPress={onClose} />
            <NavigationMenuPanel isClosing={isClosing} panelWidth={panelWidth}>
              <VStack flex={1} style={{ paddingTop: safeAreaTop }}>
                <Box
                  position="relative"
                  padding={4}
                  paddingBottom={0}
                  zIndex={10}
                >
                  <NavigationMenuClubCard
                    sessionRef={session}
                    onNavigate={onClose}
                  />
                  <Box position="absolute" top={24} left={24}>
                    <Button
                      variant="white"
                      size="tiny"
                      onPress={onClose}
                      accessibilityLabel={t`Close menu`}
                      leftIcon={<CloseIcon color="text-primary" />}
                    />
                  </Box>
                </Box>

                <VStack marginTop={6} paddingHorizontal={4}>
                  {allItems.map((item, i) =>
                    item.isDivider ? (
                      <Box
                        key={i}
                        height={1}
                        backgroundColor="surface-dividers"
                        marginVertical={1}
                      />
                    ) : (
                      <NavigationMenuItem
                        key={i}
                        label={item.label}
                        icon={item.icon}
                        selected={item.selected}
                        onPress={() => handleItemPress(item.routeHandler)}
                        testID={
                          item.routeName
                            ? `tab-bar-item-${item.routeName}`
                            : undefined
                        }
                      />
                    )
                  )}
                </VStack>

                {!isIpadOrSmaller && <Box flex={1} />}

                {!isMember && (
                  <Box paddingHorizontal={4} paddingTop={6} paddingBottom={4}>
                    <NavigationMenuJoinCard onSignIn={onSignIn} />
                  </Box>
                )}
              </VStack>
            </NavigationMenuPanel>
          </Box>
        </Modal>
      )}
      <MemberListModal
        isOpen={isMemberModalOpen}
        onClose={() => setIsMemberModalOpen(false)}
      />
    </>
  );
};

export const NavigationMenuFragmentString = graphql`
  fragment NavigationMenu_session on Session {
    ...useGetTabBarItemsFragment_session
    ...NavigationMenuClubCard_session
  }
`;
